import React, { PureComponent } from 'react';
import { array, func, oneOf, string } from 'prop-types';
import classNames from 'classnames';
import { differenceBy, get, noop } from 'lodash';
import { Button, Tooltip } from '@fiverr-private/fit';
import { I18n } from '@fiverr-private/fiverr_context';
import { prettyFileSize, uid } from '@fiverr-private/futile';
import { fileSizeFormat } from '@fiverr-private/localization';
import { filerrUploaderOptions, uploadButton } from '../lib/propTypes';
import createFilerrUploader from '../utils/createFilerrUploader';
import { basename, extname } from '../utils/file';
import UploadButton from './UploadButton';
import { DEFAULT_PROGRESS, MAX_FILE_SIZE_IN_GB, PREVIEW_TRANSFORMATION_TYPES } from './constants';

import './style.scss';

const extractRemovedAttachments = (attachments, previousAttachments) =>
    differenceBy(previousAttachments, attachments, 'id');

class FileUploader extends PureComponent {

    constructor(props) {
        super(props);

        const { options } = props;

        this.uploaders = {};
        this.filerrUploader = createFilerrUploader(options);

        this.onUpload = this.onUpload.bind(this);
    }

    componentDidUpdate(prevProps) {
        const { attachments } = this.props;

        const removedAttachments = extractRemovedAttachments(attachments, prevProps.attachments);
        this.removeAttachments(removedAttachments);
    }

    onUpload({ target }) {
        const { files } = target;

        this.uploadAttachments(files);
    }

    uploadAttachments(files) {
        const { onAttachmentChange } = this.props;

        Object.values(files).forEach((file) => {
            const id = uid();
            const attachment = this.uploadAttachment(id, file);
            onAttachmentChange(attachment);
        });
    }

    uploadAttachment(id, file) {
        this.uploaders[id] = this.filerrUploader.upload({
            file,
            onSuccess: (uploadedData) => this.onUploadSuccess(id, uploadedData),
            onError: (error) => this.onUploadError(id, error),
            onProgress: (pendingAttachmentData) => this.onUploadOnProgress(id, pendingAttachmentData)
        });

        return {
            id,
            name: file.name,
            size: file.size,
            type: file.type,
            sortId: id,
            fileName: basename(file.name),
            extension: extname(file.name),
            fileSize: fileSizeFormat(file.size, {
                fallbackValue: prettyFileSize(file.size)
            }),
            progress: DEFAULT_PROGRESS
        };
    }

    onUploadSuccess(id, { pendingAttachmentData }) {
        this.notifyAttachmentUpdate(id, {
            pendingAttachmentId: pendingAttachmentData.pending_attachment_id,
            coverImageUrl: this.coverImageUrl(pendingAttachmentData),
            loaded: true,
            ...pendingAttachmentData
        });
    }

    coverImageUrl(pendingAttachmentData) {
        const { previewTransformationType } = this.props;

        return get(pendingAttachmentData, [
            'preview_urls',
            previewTransformationType
        ]);
    }

    onUploadError(id, error) {
        this.notifyAttachmentUpdate(id, { error });
    }

    onUploadOnProgress(id, { percentage }) {
        const progress = percentage || DEFAULT_PROGRESS;

        this.notifyAttachmentUpdate(id, { progress });
    }

    notifyAttachmentUpdate(id, pendingAttachmentData = {}) {
        const { onAttachmentChange } = this.props;

        const attachment = this.findAttachmentById(id);

        if (attachment) {
            const newAttachment = { ...attachment, ...pendingAttachmentData };
            const { loaded, error } = newAttachment;

            if (loaded || error) {
                delete newAttachment.progress;
            }

            onAttachmentChange(newAttachment);
        }
    }

    findAttachmentById(attachmentId) {
        const { attachments } = this.props;

        return attachments.find(({ id }) => id === attachmentId);
    }

    removeAttachments(removedAttachments) {
        removedAttachments.forEach(({ id }) => {
            const uploader = this.uploaders[id];
            uploader?.abort();
            delete this.uploaders[id];
        });
    }

    tooltipContent() {
        const { filesLimit } = this.props;

        return (
            <>
                {
                    filesLimit && <p>
                        <I18n k="file_uploader.upload_button.max_files_and_size" params={{ limit: filesLimit, max_size: MAX_FILE_SIZE_IN_GB }}/>
                    </p>
                }
                {
                    !filesLimit && <I18n k="file_uploader.upload_button.max_size" params={{ max_size: MAX_FILE_SIZE_IN_GB }}/>
                }
            </>
        );
    }

    render() {
        const {
            size,
            color,
            title,
            icon,
            isDisabled,
            multiple,
            className,
            showTitle,
            onClick
        } = this.props;

        const tooltipBoxClasses = classNames(
            'file-uploader-tooltip-box',
            { disabled: isDisabled }
        );

        return (
            <Tooltip content={this.tooltipContent()}
                className="file-uploader-tooltip"
                contentClassName="file-uploader-tooltip-content"
                containerClassName="file-uploader-tooltip-container"
                boxClassName={tooltipBoxClasses}
                boxContentClassName="file-uploader-tooltip-box-content">
                <UploadButton className={className}
                    onUpload={this.onUpload}
                    onClick={onClick}
                    isDisabled={isDisabled}
                    size={size}
                    color={color}
                    multiple={multiple}
                    title={title}
                    showTitle={showTitle}
                    icon={icon}/>
            </Tooltip>
        );
    }
}

FileUploader.propTypes = {
    className: string,
    options: filerrUploaderOptions.isRequired,
    attachments: array,
    onAttachmentChange: func,
    previewTransformationType: oneOf(Object.values(PREVIEW_TRANSFORMATION_TYPES)),
    ...uploadButton
};

FileUploader.defaultProps = {
    attachments: [],
    onAttachmentChange: noop,
    onClick: noop,
    previewTransformationType: PREVIEW_TRANSFORMATION_TYPES.ORIGINAL
};

Object.assign(FileUploader, {
    PREVIEW_TRANSFORMATION_TYPES,
    COLORS: Button.COLORS,
    SIZES: Button.SIZES
});

export default FileUploader;
